import { useTranslation } from "react-i18next"
import PageTitle from "../../components/PageTitle"
import WhiteSpace from "../../components/Whitespace"
import Comm from "../../Comm"
import { useEffect, useState } from "react"
import ChargeType from "../../types/ChargeType"
import ChargerType from "../../types/ChargerType"
import styled from "styled-components"
import Dropdown from "../../components/Dropdown"
import LoaderDots from "../../components/LoaderDots"
import Modal from "../../components/Modal"
import React from "react"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import Icon from "../../Icon"
import secondsToHumanReadable from "../../utils/secondsToHumanReadable"
import { getAuth } from "firebase/auth"
import fbConf from "../../FirebaseConf"
import { child, get, getDatabase, ref } from "firebase/database"
import DateRangePickerWithPresets from "../../components/DateRangePickerWithPresets"
import { useNotification } from "../../modules/Notification"
import downloadObjectInBrowser from "../../utils/downloadObjectInBrowser"
import { BREAKPOINTS } from "../../GlobalTheme"
import { formatCost, formatPrice, normalizeCurrency } from "../../utils/currency"

const ToolbarContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 12px;
  display: flex;
  /* Absolutely positioned toolbars cannot wrap, so on a phone the button row overlays the page
     title and its leading buttons land at negative x, which no amount of panning can reach.
     Put it back in flow and let it wrap. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    right: auto;
    left: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

/* Owns the page's vertical space so the table below can take what the toolbars leave, instead of a
   magic calc() that guessed low at 1280px and pushed the pagination row below the visible shell. */
const PageColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: block;
    height: auto;
  }
`

const StatCardTable = styled.table`
  width: 100%;

  /* Five 20% cells in a single non-wrapping row, each holding a ~270px card, so the row needs
     ~1400px. Lay them out two-up on a phone. The row has to leave the table layout algorithm for
     that, which means the table and tbody must become blocks too, and the cell widths are inline
     percentages, hence !important. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: block;

    tbody {
      display: block;
    }

    tr {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    td {
      display: block;
      width: auto !important;
    }
  }
`

const StatCard = styled.div<{ $color: string }>`
  border-radius: 24px;
  padding: 24px;
  background-color: ${props => props.theme.colors[props.$color]};
  margin-left: 8px;
  margin-right: 8px;
  display: flex;

  /* The grid supplies the gap once the cards go two-up, and 24px of padding is most of a 183px card. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    margin-left: 0px;
    margin-right: 0px;
    padding: 16px;
    box-sizing: border-box;
  }
`

const StatCardIconContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  width: 24px;
  height: 24px;
  padding: 7px;
  border-radius: 100px;
  margin-top: 5px;
`

const StatCardTitle = styled.p<{ $white?: boolean }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.$white ? props.theme.colors.white : props.theme.colors.dark};
  margin: 0px;
  margin-left: 18px;
`

const StatCardValue = styled.p<{ $white?: boolean }>`
  font-weight: 700;
  font-size: 24px;
  color: ${props => props.$white ? props.theme.colors.white : props.theme.colors.dark};
  margin: 0px;
  margin-left: 18px;
  margin-top: 6px;
`

const StatCardUnit = styled.span`
  font-weight: 500;
  font-size: 15px;
  padding-left: 6px;
`

const ModalTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
  width: min(max(20vw, 256px), calc(100vw - 24px));
`

const ModalContainer = styled.div`
  padding: 24px;
  height: 85dvh;
  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* A fixed height with no overflow of its own caps the parent Modal's scroll area, so anything
       past it is clipped AND unreachable. Size to content and let Modal's max-height scroll it. */
    height: auto;
  }
`

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const ChargeDetailContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
`

const ChargeDetailLabel = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
  margin-left: 8px;
`

const ChargeDetailValue = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  position: absolute;
  right: 0px;
  margin: 0px;
  margin-top: 4px;
`

const ChargeDetailsPeriodContainer = styled.div`
  box-shadow: 0px 0px 10px 0px #0000001A;
  border-radius: 16px;
  padding: 20px;
  width: max(calc(20vw - 40px), 216px);
`

const ChargeDetailsPeriodTitle = styled.p`
  font-weight: 700;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const ChargeDetailsPeriodTimeLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const ChargeDetailsPeriodsContainer = styled.div`  
  padding: 10px;
  margin-left: -10px;
  margin-right: -10px;
`

const EmptyTableLabel = styled.p`
  width: 100%;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_900};
  margin-top: 48px;
  position: absolute;
`

const SearchbarLevelToolbarContainer = styled.div`
  position: absolute;
  margin-left: 15vw;
  margin-top: 8px;
  z-index: 1;
  display: flex;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    margin-left: 0px;
    flex-wrap: wrap;
    row-gap: 8px;
  }
`

const InvalidCdrLabel = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.danger};
  margin: 0px;
  width: 100%;
  text-align: center;
`

const NoticeLabel = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.warning};
  margin: 0px;
  width: 100%;
  text-align: center;
`

function translatePStmode(mode: number): string {
  switch (mode) {
    case 0: return 'Autostart'
    case 1: return 'Manual'
    case 2: return 'Scheduled'
    case 3: return 'Supplier'
    case 4: return 'Power Sharing'
    case 5: return 'Restart broken'
    case 6: return 'HTTP API'
    case 7: return 'Prepaid RFID'
    case 8: return 'Home RFID'
    case 9: return 'OCPP'
    default: return `Unknown (${mode})`
  }
}

// Charge stop reason (p_stop_ev) user-facing texts, from the TChargeStopReason table owned by Will Gábor
// (https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/368869378). HU + EN are provided there;
// long term these should come from Voltie Local, shared with the apps (VLT-2085).
const STOP_REASONS: { [key: number]: { hu: string, en: string } } = {
  0: { hu: 'Nincs vége, töltés folyamatban', en: 'Not ended, charging in progress' },
  1: { hu: 'Egyéb', en: 'Other' },
  2: { hu: 'Idő limit elérve', en: 'Time limit reached' },
  3: { hu: 'Energia limit elérve', en: 'Energy limit reached' },
  4: { hu: 'Felhasználó által leállítva', en: 'Stopped by user' },
  5: { hu: 'Hiba: szivárgóáram (GFCI-szenzor)', en: 'Error: residual current detected (GFCI sensor)' },
  6: { hu: 'Töltő letiltva', en: 'Charger disabled' },
  7: { hu: 'Firmware frissítés miatt újraindult', en: 'Restart due to firmware update' },
  8: { hu: 'Töltő alvó módban', en: 'Charger in sleep mode' },
  9: { hu: 'Hiba: kimeneti feszültség (földelés)', en: 'Error: output voltage (grounding)' },
  10: { hu: 'Kábel kihúzva', en: 'Cable unplugged' },
  11: { hu: 'Jármű által leállítva', en: 'Stopped by vehicle' },
  12: { hu: 'Hiba: HPOW I2C-busz', en: 'Error: HPOW I2C bus fault' },
  13: { hu: 'Hiba: GFCI önteszt', en: 'Error: GFCI self-test' },
  14: { hu: 'Hiba: túlmelegedés', en: 'Error: overheating' },
  15: { hu: 'Hiba: dióda ellenőrzés', en: 'Error: diode check' },
  16: { hu: 'Hiba: földelés', en: 'Error: grounding' },
  17: { hu: 'Hiba: beragadt relé', en: 'Error: stuck relay' },
  18: { hu: 'Hiba: túláram', en: 'Error: overcurrent fault' },
  19: { hu: 'Hiba: I2C-busz', en: 'Error: I2C bus' },
  20: { hu: 'Hiba: külső árammérő', en: 'Error: external meter' },
  21: { hu: 'Hiba: magas páratartalom', en: 'Error: high humidity' },
  22: { hu: 'Hiba: helytelen fázisbekötés', en: 'Error: incorrect phase order' },
  23: { hu: 'Szerver által leállítva', en: 'Stopped by server' },
  24: { hu: 'Belső hibakód', en: 'Internal error code' },
  25: { hu: 'Belső hibakód', en: 'Internal error code' },
  26: { hu: 'Belső hibakód', en: 'Internal error code' },
  27: { hu: 'Szándékos szoftveres leállítás/újraindítás', en: 'Intentional SW shutdown/restart' },
  28: { hu: 'RFID-s indításra lejárt az idő', en: 'RFID start timed out' },
  29: { hu: 'RFID-s töltés vége (RFID-val leállítva)', en: 'RFID charge ended (stopped by RFID)' },
  30: { hu: 'Szolgáltatói újraindítás', en: 'Restart by service provider' },
  31: { hu: 'Áramkimaradás', en: 'Power outage' },
  32: { hu: 'HTTP API-n keresztül leállítva', en: 'Stopped via HTTP API' },
  33: { hu: 'OCPP tranzakció vége', en: 'OCPP transaction stopped' },
  34: { hu: 'PPRFID master által leállítva', en: 'Stopped by PPRFID master' },
  35: { hu: 'PPRFID master kapcsolat megszakadt', en: 'PPRFID master connection lost' },
  36: { hu: 'PPRFID egyenleg elfogyott', en: 'PPRFID balance depleted' },
  37: { hu: 'RFID-vel leállítva (kártya újraolvasása)', en: 'Stopped by RFID (card read again)' },
  100: { hu: 'Nincs elég szabad épületáram', en: 'Not enough building current available' },
  101: { hu: 'Nincs elég napenergia', en: 'Not enough solar energy available' },
  102: { hu: 'Nem elég magas a hálózati feszültség', en: 'Grid voltage is not high enough' },
  103: { hu: 'Töltőáram limit nulla', en: 'Zero max charge current' },
  104: { hu: 'MID-mérő nem elérhető', en: 'MID meter unavailable' },
  105: { hu: 'Túlfeszültség', en: 'Overvoltage' },
  106: { hu: 'Jármű hiba (E)', en: 'Vehicle error (E)' },
  107: { hu: 'Alacsony feszültség', en: 'Undervoltage' },
  108: { hu: 'Jármű D állapot', en: 'Vehicle D state' },
  109: { hu: 'Nem azonosítható nagyfesz panel', en: 'HPOW board unidentified' },
}

function translateStopReason(reason: number, lang: string): string {
  const entry = STOP_REASONS[reason]
  const fallback = { hu: 'Egyéb ok', en: 'Other reason' }
  return (entry || fallback)[lang === 'hu' ? 'hu' : 'en']
}

export default function Charges({ charger, isAdmin }: { charger: ChargerType, isAdmin: boolean }) {
  const { t, i18n } = useTranslation()
  const { addNotification } = useNotification()

  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>()

  const [charges, setCharges] = useState<ChargeType[] | null>()
  const [chargesInSelectedDateRange, setChargesInSelectedDateRange] = useState<ChargeType[] | null>()
  const [lastRefresh, setLastRefresh] = useState<string>('')

  const [cdrStats, setCdrStats] = useState<{ lifetimeChgSec: number, lifetimeWh: number }>()
  const [allIdleTime, setAllIdleTime] = useState<number>()

  const [isShowingChargeDetails, setIsShowingChargeDetails] = useState<ChargeType | null>()
  //const [isShowingExportChargeExportModalForAdmins, setIsShowingExportChargeExportModalForAdmins] = useState<ChargeType | null>()

  // Admins can hide the invalid (error) CDRs from the list with a toggle (VLT-1192)
  const [hideInvalidCharges, setHideInvalidCharges] = useState<boolean>(false)

  // Effective kWh price (charger-specific if set, otherwise the user's uniform price) for showing charge cost (VLT-2127)
  const [kwhPrice, setKwhPrice] = useState<{ currency: string, value: number }>()


  async function fetchCharges() {
    const valid_response = await Comm('/api-cdr/cdrs/last', {
      headers: { "Charger_ID": charger.chargerId }
    })
    const valid_result: ChargeType[] = await valid_response.json()

    // Fetch invalid CDRs for admins only
    let invalid_result = []
    if (isAdmin) {
      const invalid_response = await Comm('/api-cdr/cdrs/last/errors', {
        headers: { "Charger_ID": charger.chargerId }
      })
      if (invalid_response.status == 200) {

        invalid_result = await invalid_response.json()
        for (let i = 0; i < invalid_result.length; i++) {
          invalid_result[i] = JSON.parse(invalid_result[i])
        }
        (invalid_result as ChargeType[]).map(invalidCrd => {
          invalidCrd.invalid = true
          if (!invalidCrd.periods) {
            invalidCrd.periods = []
          }
        })
        //console.log(invalid_result)
      }
    }
    const result = [...valid_result, ...invalid_result]

    result.sort((a: ChargeType, b: ChargeType) => b.cdr_id - a.cdr_id)
    setCharges(result)
    // All charges are in the date range by default
    setChargesInSelectedDateRange(result)
    if (result.length > 0) {
      setSelectedDateRange([new Date(result[result.length - 1].s_start * 1000), new Date(result[0].s_end * 1000)])
    } else {
      setSelectedDateRange([new Date()])
    }
    setLastRefresh(new Date().toString().split(' ')[4])


    // Calculate stats
    let idleTime = 0
    for (const charge of result) {
      idleTime += charge.idle_time / 1000
    }
    setAllIdleTime(idleTime)

    // Fetch CDR stats if charger is online for stats in the top row (wait two seconds to make sure that charger is ready to respond)
    setTimeout(() => {
      if (charger.online) {
        fetchCdrStats()
      }
    }, 2000)
  }
  useEffect(() => {
    fetchCharges()
    fetchKwhPrice()
  }, [])

  async function fetchKwhPrice() {
    const dbRef = ref(getDatabase(fbConf))
    const uid = isAdmin ? charger.userId : getAuth(fbConf).currentUser?.uid

    // Use the charger-specific price if one is set, otherwise fall back to the user's uniform price
    const specific = await get(child(dbRef, `voltie/chargerusers/${uid}/chargers/${charger.chargerId}/kwh_price`))
    if (specific.exists()) {
      const val = specific.val()
      setKwhPrice({ ...val, currency: normalizeCurrency(val.currency) })
      return
    }

    const uniform = await get(child(dbRef, `voltie/chargerusers/${uid}/preferences/calculation_values/price`))
    const val = uniform.val()
    if (val) setKwhPrice({ ...val, currency: normalizeCurrency(val.currency) })
  }

  function handelRefresh() {
    setCharges(null)
    setChargesInSelectedDateRange(null)
    fetchCharges()
  }

  // Email export, only for Users
  async function handleExportChargeForUsers(charge: ChargeType) {
    const userId = getAuth(fbConf).currentUser?.uid
    if (userId) {
      addNotification(t('charger_sendingViaEmail'), "TEXT")
      const response = await Comm(`/api-financial/report/excel/${charge.cdr_id}`, {
        headers: { "user_id": userId, "Content-Type": "application/json" }
      })
      if (response.ok) {
        addNotification(t('charger_theExportedExcelFileHasBeenSentViaEmail'), "TEXT")
      } else {
        addNotification('Error while sending email', "TEXT", "error")
      }
    }
  }

  // Email export, only for Users
  async function handleExportChargesForUsers() {
    const userId = getAuth(fbConf).currentUser?.uid
    if (userId && charges && selectedDateRange) {
      addNotification(t('charger_sendingViaEmail'), "TEXT")
      const response = await Comm(`/api-financial/report/excel?from=${Math.round((selectedDateRange[0].getTime() - 86400000) / 1000)}&to=${Math.round(selectedDateRange[1].getTime() / 1000) - 1}`, {
        headers: { "user_id": userId, "Content-Type": "application/json" }
      })
      if (response.ok) {
        addNotification(t('charger_theExportedExcelFileHasBeenSentViaEmail'), "TEXT")
      } else {
        addNotification('Error while sending email', "TEXT", "error")
      }
    }
  }

  async function handleExportChargeForAdmins(charge: ChargeType) {
    downloadObjectInBrowser(charge, `CDR ${charge.cdr_id} - ${charge.charger_id}`)
  }

  // Direct xls download from the browser (instead of email / client-side generation), via the backend's download=true branch (VLT-2041)
  async function handleDownloadChargesReport() {
    // Admins download the report for the charger's owner; users download their own
    const userId = isAdmin ? charger.userId : getAuth(fbConf).currentUser?.uid
    if (!userId) return

    // Nothing to export for an empty period / missing range
    if (!chargesInSelectedDateRange || chargesInSelectedDateRange.length === 0 || !selectedDateRange || !selectedDateRange[1]) {
      addNotification(t('charger_noChargesInSelectedPeriod'), "TEXT", "error")
      return
    }

    const from = Math.round((selectedDateRange[0].getTime() - 86400000) / 1000)
    const to = Math.round(selectedDateRange[1].getTime() / 1000) - 1

    // The report can take a while to generate; let the user know the download is being prepared
    addNotification(t('charger_preparingTheDownload'), "TEXT")

    const response = await Comm(`/api-financial/report/excel?from=${from}&to=${to}`, {
      headers: { "user_id": userId, "Content-Type": "application/json", "download": true }
    })

    if (response.ok) {
      // Wrap the bytes in a blob with the explicit xlsx MIME type so the file opens on click (not as .bin)
      const blob = new Blob([await response.arrayBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const downloadAnchorNode = document.createElement('a')
      downloadAnchorNode.setAttribute("href", url)
      downloadAnchorNode.setAttribute("download", `Voltie report ${selectedDateRange[0].toISOString().split('T')[0]} - ${selectedDateRange[1].toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(downloadAnchorNode) // required for firefox
      downloadAnchorNode.click()
      downloadAnchorNode.remove()
      URL.revokeObjectURL(url)
    } else {
      addNotification(t('charger_errorWhileDownloadingTheReport'), "TEXT", "error")
    }
  }

  async function fetchCdrStats() {
    const CMD_GET_PRODUCT_NFO_response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 12 })
    })
    const CMD_GET_PRODUCT_NFO_result = await CMD_GET_PRODUCT_NFO_response.json()

    setCdrStats({
      lifetimeChgSec: CMD_GET_PRODUCT_NFO_result.lifetime?.lifetime_chg_sec || 0,
      lifetimeWh: CMD_GET_PRODUCT_NFO_result.lifetime?.lifetime_Wh
    })
  }

  function handleChooseDateRange(range: Date[]) {
    if (!range[1]) {
      range[1] = new Date(range[0].getTime() + 86400000)
    }

    if (charges) {
      setSelectedDateRange(range)
      const chargesInRange = []
      const rangeStartUnix = Math.round((range[0].getTime() - 86400000) / 1000)
      const rangeEndUnix = Math.round(range[1].getTime() / 1000)
      for (const charge of charges) {
        if (charge.s_start >= rangeStartUnix && charge.s_end <= rangeEndUnix) {
          chargesInRange.push(charge)
        }
      }
      setChargesInSelectedDateRange(chargesInRange)
    }
  }

  // Only admins receive invalid CDRs, so the hide toggle is admin-only (VLT-1192)
  const displayedCharges = chargesInSelectedDateRange && hideInvalidCharges
    ? chargesInSelectedDateRange.filter(charge => !charge.invalid)
    : chargesInSelectedDateRange

  const hideInvalidChargesToggle = isAdmin ? (
    <ButtonWithIcon icon="general.filter" label={t('charger_hideInvalidCharges')} onClick={() => setHideInvalidCharges(prev => !prev)} backgroundColor={hideInvalidCharges ? 'primary' : 'dark'} iconColor="white" />
  ) : undefined

  return (
    <PageColumn>
      <WhiteSpace height={'12px'} />
      <PageTitle label={t('charger_charges')} />
      {/*{isShowingExportChargeExportModalForAdmins && <>
        {console.log('Lool')}
        <ExportButton fileName={`CDR ${isShowingExportChargeExportModalForAdmins.cdr_id}`} handleAfterDone={() => setIsShowingExportChargeExportModalForAdmins(null)} modalOnly titleRow={[
          { label: 'CDR ID', width: '10' },
          { label: 'Start', width: '20' },
          { label: 'Stop', width: '20' },
          { label: 'All Energy', width: '15' },
          { label: 'Charge Time', width: '15' },
          { label: 'Idle Time', width: '15' },
          { label: 'Average Power', width: '15' },
          { label: 'RFID Name', width: '25' },
          { label: 'Charger ID', width: '20' },
          { label: 'Number of Periods', width: '20' },
          { label: 'Periods', width: '150' }
        ]} data={[isShowingExportChargeExportModalForAdmins].map(cdr => [cdr.cdr_id, new Date(cdr.s_start * 1000).toLocaleString('hu'), new Date(cdr.s_end * 1000).toLocaleString('hu'), `${+cdr.chg_energy.toFixed(2)} kWh`, secondsToHumanReadable(cdr.chg_time).replace(/\n/g, '').replace(/ /g, '').replace(/h/g, 'h '), secondsToHumanReadable(cdr.idle_time).replace(/\n/g, '').replace(/ /g, '').replace(/h/g, 'h '), `${+cdr.avg_power.toFixed(2)} kWh`, cdr.idtag_name, cdr.charger_id, cdr.periods.length, JSON.stringify(cdr.periods).replace(/\},{/g, '},\n{')])} />
      </>}*/}
      {isShowingChargeDetails && (
        <Modal align="right" onEscape={() => setIsShowingChargeDetails(null)}>
          <ModalContainer style={{ position: 'relative', overflowY: 'scroll', overflowX: 'hidden' }}>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingChargeDetails(null)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_chargeDetails')}</ModalTitle>
            
            {isShowingChargeDetails.invalid ? <>
              <WhiteSpace height="22px" />
              <InvalidCdrLabel>{isShowingChargeDetails.invalid_rsn || 'No invalid reason specified'}</InvalidCdrLabel>
              <WhiteSpace height="22px" />
            </> : <>
              <WhiteSpace height="28px" />
            </>}
            {isShowingChargeDetails.closed_after_restart ? <>
              <NoticeLabel>{t('charger_closedDueToPowerOutage')}</NoticeLabel>
              <WhiteSpace height="16px" />
            </> : null}
            {[
              { icon: "chargers.rfidTag", label: t('charger_rfid'), value: isShowingChargeDetails.idtag_name || '-' },
              { icon: "chargers.rfidTag", label: t('general_number'), value: isShowingChargeDetails.idtag || '-' },
              { icon: "chargers.pluggedIn", label: t('charger_pluggedIn'), value: new Date(isShowingChargeDetails.s_start * 1000).toLocaleTimeString('hu') },
              { icon: "chargers.unplugged", label: t('charger_unplugged'), value: new Date(isShowingChargeDetails.s_end * 1000).toLocaleTimeString('hu') },
              { icon: "chargers.time", iconColor: 'primary', label: t('charger_chargingTime'), value: secondsToHumanReadable(isShowingChargeDetails.chg_time) },
              { icon: "chargers.sleep", iconColor: 'gray_500', label: t('charger_idleTime'), value: secondsToHumanReadable(isShowingChargeDetails.idle_time) },
              { icon: "chargers.energy", iconColor: 'warning', label: t('charger_energy'), value: `${isShowingChargeDetails.chg_energy} kWh` },
              { icon: "chargers.average", label: t('charger_averagePower'), value: `${isShowingChargeDetails.avg_power} kW` },
              { hidden: isShowingChargeDetails.max_power == null, icon: "chargers.average", iconColor: 'primary', label: t('charger_maxPower'), value: `${isShowingChargeDetails.max_power} kW` },
              { icon: "general.money", iconColor: 'primary', label: t('charger_kwhPrice'), value: kwhPrice ? `${formatPrice(Number(kwhPrice.value), kwhPrice.currency, i18n.language)} ${kwhPrice.currency}/kWh` : '-' },
              { icon: "general.money", iconColor: 'primary', label: t('charger_cost'), value: kwhPrice ? `${formatCost(kwhPrice.value * isShowingChargeDetails.chg_energy, kwhPrice.currency, i18n.language)} ${kwhPrice.currency}` : '-' },

              // MID meter readings, shown to everyone but only when the session had a certified meter (VLT-2085)
              { hidden: !isShowingChargeDetails.certified_meter, icon: "chargers.energy", iconColor: 'warning', label: t('charger_midMeterStart'), value: `${isShowingChargeDetails.meter_start_kwh} kWh` },
              { hidden: !isShowingChargeDetails.certified_meter, icon: "chargers.energy", iconColor: 'warning', label: t('charger_midMeterStop'), value: `${isShowingChargeDetails.meter_stop_kwh} kWh` },

              { adminOnly: true, icon: "chargers.humidity", iconColor: 'primary', label: `${t('charger_humidity')} (max/min)`, value: `${isShowingChargeDetails.hum_max}% / ${isShowingChargeDetails.hum_min}%` },
              { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `PI (max/${t('charger_avg')})`, value: `${isShowingChargeDetails.temp_pi_max} °C / ${isShowingChargeDetails.temp_pi_avg} °C` },
              { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `POW (max/${t('charger_avg')})`, value: `${isShowingChargeDetails.temp_pow_max} °C / ${isShowingChargeDetails.temp_pow_avg} °C` },
              { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `UC (max/${t('charger_avg')})`, value: `${isShowingChargeDetails.temp_uc_max} °C / ${isShowingChargeDetails.temp_uc_avg} °C` },

              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'cdr_id', value: isShowingChargeDetails.cdr_id || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'cdr_ver', value: isShowingChargeDetails.cdr_ver || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'charger_id', value: isShowingChargeDetails.charger_id || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'owner', value: isShowingChargeDetails.owner || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'user', value: isShowingChargeDetails.user || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'phase', value: isShowingChargeDetails.phase || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'sw_ver', value: isShowingChargeDetails.sw_ver || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'fw_ver', value: isShowingChargeDetails.fw_ver || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'last_update', value: new Date(isShowingChargeDetails.last_update * 1000).toLocaleString('hu') || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'transaction_id', value: isShowingChargeDetails.transaction_id || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'transaction_end', value: isShowingChargeDetails.transaction_end || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'certified_meter', value: isShowingChargeDetails.certified_meter?.toString() || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'user_id', value: isShowingChargeDetails.user_id || '-' },
              { adminOnly: true, icon: "general.empty", iconColor: 'primary', label: 'sec_user_id', value: isShowingChargeDetails.sec_user_id || '-' },
              
            ].map(dataPoint => !dataPoint.hidden && ((dataPoint.adminOnly && isAdmin) || (!dataPoint.adminOnly)) ? (
              <React.Fragment key={dataPoint.label}>
                <ChargeDetailContainer>
                  <Icon path={dataPoint.icon} color={dataPoint.iconColor} width="24px" height="24px" />
                  <ChargeDetailLabel>{dataPoint.label}</ChargeDetailLabel>
                  <ChargeDetailValue>{dataPoint.value}</ChargeDetailValue>
                </ChargeDetailContainer>
                <WhiteSpace height="16px" />
              </React.Fragment>
            ) : null)}
            <WhiteSpace height="16px" />
            <ChargeDetailsPeriodsContainer style={{ maxHeight: `calc(85vh - 383px - ${isShowingChargeDetails.invalid ? '32px' : '0px'} - ${isAdmin ? '160px' : '0px'})`, overflowY: 'scroll' }}>
              {isShowingChargeDetails.periods.map(period => (
                <React.Fragment key={`period${period.index}`}>
                  <ChargeDetailsPeriodContainer>
                    <ChargeDetailsPeriodTitle>{`${t('charger_period')} ${period.index + 1}`}</ChargeDetailsPeriodTitle>
                    <ChargeDetailsPeriodTimeLabel>{`${new Date(period.p_start * 1000).toISOString().split('T')[1].split('.')[0]} - ${new Date(period.p_end * 1000).toISOString().split('T')[1].split('.')[0]}`}</ChargeDetailsPeriodTimeLabel>
                    <ChargeDetailsPeriodTimeLabel>{translatePStmode(period.p_stmode)}</ChargeDetailsPeriodTimeLabel>
                    <WhiteSpace height="16px" />
                    <ChargeDetailContainer>
                      <Icon path="chargers.energy" color="warning" width="24px" height="24px" />
                      <ChargeDetailLabel>{t('charger_energy')}</ChargeDetailLabel>
                      <ChargeDetailValue>{`${period.p_energy} kWh`}</ChargeDetailValue>
                    </ChargeDetailContainer>
                    {period.p_stop_ev ? <>
                      <WhiteSpace height="16px" />
                      <ChargeDetailContainer>
                        <Icon path="chargers.power" width="24px" height="24px" />
                        <ChargeDetailLabel>{t('charger_stopReason')}</ChargeDetailLabel>
                        <ChargeDetailValue>{translateStopReason(period.p_stop_ev, i18n.language)}</ChargeDetailValue>
                      </ChargeDetailContainer>
                    </> : null}
                    {period.p_lim_time > 0 ? <>
                      <WhiteSpace height="16px" />
                      <ChargeDetailContainer>
                        <Icon path="chargers.time" color="primary" width="24px" height="24px" />
                        <ChargeDetailLabel>{t('charger_timeLimit')}</ChargeDetailLabel>
                        <ChargeDetailValue>{secondsToHumanReadable(period.p_lim_time)}</ChargeDetailValue>
                      </ChargeDetailContainer>
                    </> : null}
                    {period.p_lim_energy > 0 ? <>
                      <WhiteSpace height="16px" />
                      <ChargeDetailContainer>
                        <Icon path="chargers.energy" color="warning" width="24px" height="24px" />
                        <ChargeDetailLabel>{t('charger_energyLimit')}</ChargeDetailLabel>
                        <ChargeDetailValue>{`${period.p_lim_energy} kWh`}</ChargeDetailValue>
                      </ChargeDetailContainer>
                    </> : null}
                    {period.target_percent > 0 ? <>
                      <WhiteSpace height="16px" />
                      <ChargeDetailContainer>
                        <Icon path="chargers.average" width="24px" height="24px" />
                        <ChargeDetailLabel>{t('charger_batteryLevel')}</ChargeDetailLabel>
                        <ChargeDetailValue>{`${period.start_percent}% → ${period.target_percent}%`}</ChargeDetailValue>
                      </ChargeDetailContainer>
                    </> : null}
                  </ChargeDetailsPeriodContainer>
                  <WhiteSpace height="16px" />
                </React.Fragment>
              ))}
            </ChargeDetailsPeriodsContainer>
          </ModalContainer>
        </Modal>
      )}
      <StatCardTable>
        <tbody>
          <tr>
            <td style={{ width: '20%' }}>
              <StatCard $color="dark">
                <StatCardIconContainer>
                  <Icon path="chargers.power" width="100%" height="100%" />
                </StatCardIconContainer>
                <div>
                  <StatCardTitle $white>{t('charger_allCharges')}</StatCardTitle>
                  <StatCardValue $white>{charges ? charges.length : <LoaderDots />}<StatCardUnit>{t('charger_pcs')}</StatCardUnit></StatCardValue>
                </div>
              </StatCard>
            </td>
            <td style={{ width: '20%' }}>
              <StatCard $color="warning">
                <StatCardIconContainer>
                  <Icon path="chargers.energy" width="100%" height="100%" color="warning" />
                </StatCardIconContainer>
                <div>
                  <StatCardTitle>{t('charger_allChargedEnergy')}</StatCardTitle>
                  <StatCardValue>{charger.online ? cdrStats ? (cdrStats.lifetimeWh / 1000).toFixed(2) : <LoaderDots /> : '-'}<StatCardUnit>kWh</StatCardUnit></StatCardValue>
                </div>
              </StatCard>
            </td>
            <td style={{ width: '20%' }}>
              <StatCard $color="primary">
                <StatCardIconContainer>
                  <Icon path="chargers.time" width="100%" height="100%" color="primary" />
                </StatCardIconContainer>
                <div>
                  <StatCardTitle>{t('charger_allChargetime')}</StatCardTitle>
                  <StatCardValue>{charger.online ? cdrStats ? secondsToHumanReadable(cdrStats.lifetimeChgSec) : <LoaderDots /> : '-h'}</StatCardValue>
                </div>
              </StatCard>
            </td>
            <td style={{ width: '20%' }}>
              <StatCard $color="dark">
                <StatCardIconContainer>
                  <Icon path="chargers.average" width="100%" height="100%" />
                </StatCardIconContainer>
                <div>
                  <StatCardTitle $white>{t('charger_averageChargedEnergy')}</StatCardTitle>
                  <StatCardValue $white>{charger.online ? cdrStats && charges ? (((cdrStats.lifetimeWh / 1000) / charges.length) || 0).toFixed(2) : <LoaderDots /> : '-'}<StatCardUnit>kWh</StatCardUnit></StatCardValue>
                </div>
              </StatCard>
            </td>
            <td style={{ width: '20%' }}>
              <StatCard $color="gray_300">
                <StatCardIconContainer>
                  <Icon path="chargers.sleep" width="100%" height="100%" color="gray_600" />
                </StatCardIconContainer>
                <div>
                  <StatCardTitle>{t('charger_allIdleTime')}</StatCardTitle>
                  <StatCardValue>{charger.online ? typeof allIdleTime === 'number' ? secondsToHumanReadable(allIdleTime) : <LoaderDots /> : '-'}</StatCardValue>
                </div>
              </StatCard>
            </td>
          </tr>
        </tbody>
      </StatCardTable>
      <ToolbarContainer>
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('charger_refresh')} onClick={handelRefresh} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
      </ToolbarContainer>
      {selectedDateRange && <>
        {/* An absolutely positioned child of a FLEX container takes its static position from the
            container's content-box start rather than from after its previous siblings, so inside
            PageColumn this row jumped up beside the page title. This plain wrapper is an in-flow flex
            item, giving the row back its position on the search bar's line; it holds only an
            out-of-flow child, so it adds no height of its own. */}
        <div>
        <SearchbarLevelToolbarContainer style={!charges ? { visibility: 'hidden' } : {}}>
          <div>
            <DateRangePickerWithPresets onSelect={(range) => handleChooseDateRange(range)} defaultDate={selectedDateRange} />
          </div>
          <WhiteSpace width="32px" />
          <div style={{ marginTop: "6px" }}>
            <div style={{ display: 'flex' }}>
              <ButtonWithIcon icon="general.download" label={t('charger_exportToExcel')} onClick={handleDownloadChargesReport} backgroundColor="dark" iconColor="white" />
              {!isAdmin && <>
                <WhiteSpace width="8px" />
                <ButtonWithIcon icon="general.export" label={t('charger_exportToEmail')} onClick={handleExportChargesForUsers} backgroundColor="white" />
              </>}
              {hideInvalidChargesToggle && <>
                <WhiteSpace width="8px" />
                {hideInvalidChargesToggle}
              </>}
            </div>
          </div>
        </SearchbarLevelToolbarContainer>
        </div>
        <WhiteSpace width="8px" />
      </>}
      <WhiteSpace height="8px" />
      {displayedCharges ? (
        <Table id="Charges" height="fill" isSearchEnabled={true} isPaginationEnabled={true} isRenderEmpty={displayedCharges.length == 0} triggerRefresh={displayedCharges.toString().length + (kwhPrice ? 1 : 0) + (hideInvalidCharges ? 1 : 0)} columns={[
          { width: '30px' },
          { label: t('charger_chargeId'), width: '8%' },
          { label: t('charger_chargingStart'), width: '10%' },
          { label: t('charger_chargingEnd'), width: '10%' },
          { label: t('charger_rfid'), width: '22%' },
          { label: t('charger_chargedEnergy'), width: '8%' },
          { label: t('charger_chargingTime'), width: '8%' },
          { label: t('charger_averagePower'), width: '8%' },
          { label: t('charger_cost'), width: '8%' },
          { label: t('charger_numberOfChargingPeriods') },
          { width: '20px' }
        ]} data={displayedCharges.length > 0 ? displayedCharges.map(charge => [
          {
            onClick: () => setIsShowingChargeDetails(charge),
            value: {
              type: TableCellTypes.Image,
              src: (() => {
                if (!charge.invalid) {
                  switch (charge.periods && charge.periods[0] && charge.periods[0].p_stop_ev ? charge.periods[0].p_stmode : false) {
                    case 2: return "chargers.scheduledCharging"
                    case 3: return "chargers.kWhBasedCharging"
                    default: return "chargers.normalCharging"
                  }
                } else {
                  return 'chargers.errorCharging'
                }
              })()
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.cdr_id,
            value: {
              type: TableCellTypes.Label,
              value: charge.cdr_id.toString()
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.s_start,
            value: {
              type: TableCellTypes.LabelDivided,
              value1: new Date(charge.s_start * 1000).toLocaleDateString('hu'),
              value2: new Date(charge.s_start * 1000).toLocaleTimeString('hu')
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.s_end,
            value: {
              type: TableCellTypes.LabelDivided,
              value1: new Date(charge.s_end * 1000).toLocaleDateString('hu'),
              value2: new Date(charge.s_end * 1000).toLocaleTimeString('hu')
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: `${charge.idtag_name} ${charge.idtag}` || '-',
            value: {
              type: TableCellTypes.LabelWithIcon,
              value: charge.idtag_name || '-',
              src: "chargers.rfidTag"
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.chg_energy,
            value: {
              type: TableCellTypes.LabelWithIcon,
              value: `${+charge.chg_energy.toFixed(2)} kWh`,
              src: "chargers.energy",
              iconColor: "warning"
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.chg_time,
            value: {
              type: TableCellTypes.LabelWithIcon,
              value: secondsToHumanReadable(charge.chg_time),
              src: "chargers.time",
              iconColor: "primary"
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.avg_power,
            value: {
              type: TableCellTypes.LabelWithIcon,
              value: `${+charge.avg_power.toFixed(2)} kW`,
              src: "chargers.average"
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: kwhPrice ? kwhPrice.value * charge.chg_energy : 0,
            value: {
              type: TableCellTypes.LabelWithIcon,
              value: kwhPrice ? `${formatCost(kwhPrice.value * charge.chg_energy, kwhPrice.currency, i18n.language)} ${kwhPrice.currency}` : '-',
              src: "general.money",
              iconColor: "primary"
            }
          }, {
            onClick: () => setIsShowingChargeDetails(charge),
            sortBy: charge.periods ? charge.periods.length : 0,
            value: {
              type: TableCellTypes.Label,
              value: charge.periods ? charge.periods.length.toString() : '0'
            }
          }, {
            custom: <Dropdown handleChoose={(value) => value == t('general_export') ? (isAdmin ? handleExportChargeForAdmins(charge) /* setIsShowingExportChargeExportModalForAdmins */ : handleExportChargeForUsers(charge)) : setIsShowingChargeDetails(charge)} title={{ icon: "general.more", label: '' }} options={[{ icon: "general.log", label: t('charger_details') }, { icon: "general.export", label: t('general_export') }]} showOptionContainerOnLeft />
          }
        ]) : [[{}, { sortBy: 0 }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: '' }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: 0 }, {}]]} />
      ) : <EmptyTableLabel><LoaderDots /></EmptyTableLabel>}
    </PageColumn>
  )
}